import type { ApiClient } from '../http/apiClient';
import type { IdentityProvider } from '../../auth/identityProvider';
import { buildMetaInfo, type RequestType } from '../model/metaInfo';
import type {
  KnowledgeResponse,
  KnowledgeGetPackageByNameRequest,
  KnowledgeGetSpecPackageRequest,
  KnowledgeGetSpecPackagesRequest,
  KnowledgeGetPackageRevisionsRequest,
  KnowledgeGetLatestRevisionRequest,
  KnowledgeGetRevisionByIdRequest,
  KnowledgeGetSpecPackageDocumentsRequest,
  KnowledgeGetSpecDocRequest,
  KnowledgeListPackagesRequest,
  KnowledgeListCollectionsRequest,
  KnowledgeGetCollectionByIdRequest,
  KnowledgeGetCollectionLinksRequest,
  KnowledgeListCollectionRevisionsRequest,
  KnowledgeGetCollectionRevisionByIdRequest,
  KnowledgeGetCollectionRevisionSourcesRequest,
  KnowledgeGetCollectionElementRevisionsRequest,
  KnowledgeGetCollectionRevisionDocumentsRequest,
  KnowledgeGetCollectionRevisionDocumentContentRequest,
  KnowledgeSemanticSearchRequest,
} from '../model/types';

/**
 * Knowledge (Spec Studio) client — one method per modeled Knowledge operation
 * (19 total). Every operation returns { statusCode, body } where `body` is a
 * JSON string; use {@link KnowledgeApi.parseBody} to typed-parse it.
 *
 * Calls {@link ApiClient} only. Pure TS — no React. jobCategory is
 * KNOWLEDGE_DISCOVERY for all Knowledge requests.
 */

const PATHS = {
  getPackageByName: '/specstudio/package-by-name',
  getSpecPackage: '/specstudio/spec-package',
  getSpecPackages: '/specstudio/spec-packages',
  getPackageRevisions: '/specstudio/package-revisions',
  getLatestRevision: '/specstudio/latest-revision',
  getRevisionById: '/specstudio/revision-by-id',
  getSpecPackageDocuments: '/specstudio/spec-package-documents',
  getSpecDoc: '/specstudio/spec-doc',
  listPackages: '/specstudio/list-packages',
  listCollections: '/specstudio/list-collections',
  getCollectionById: '/specstudio/collection-by-id',
  getCollectionLinks: '/specstudio/collection-links',
  listCollectionRevisions: '/specstudio/collection-revisions',
  getCollectionRevisionById: '/specstudio/collection-revision-by-id',
  getCollectionRevisionSources: '/specstudio/collection-revision-sources',
  getCollectionElementRevisions: '/specstudio/collection-element-revisions',
  getCollectionRevisionDocuments: '/specstudio/collection-revision-documents',
  getCollectionRevisionDocumentContent:
    '/specstudio/collection-revision-document-content',
  semanticSearch: '/specstudio/semantic-search',
} as const;

export class KnowledgeApi {
  constructor(
    private readonly apiClient: ApiClient,
    private readonly identityProvider: IdentityProvider,
  ) {}

  /**
   * Typed helper: parses the JSON string `body` of a Knowledge response into T.
   */
  static parseBody<T>(response: KnowledgeResponse): T {
    return JSON.parse(response.body) as T;
  }

  /** Builds metaInfo + posts a Knowledge request; DRY core for all methods. */
  private async call<TReq extends { metaInfo: unknown }>(
    path: string,
    jobName: string,
    requestType: RequestType,
    payload: Omit<TReq, 'metaInfo'>,
  ): Promise<KnowledgeResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName,
      jobCategory: 'KNOWLEDGE_DISCOVERY',
      requestType,
    });
    return this.apiClient.post<TReq, KnowledgeResponse>(path, {
      metaInfo,
      ...payload,
    } as TReq);
  }

  getPackageByName(
    payload: Omit<KnowledgeGetPackageByNameRequest, 'metaInfo'>,
  ): Promise<KnowledgeResponse> {
    return this.call<KnowledgeGetPackageByNameRequest>(
      PATHS.getPackageByName,
      'KnowledgeGetPackageByName',
      'GET_PACKAGE_BY_NAME',
      payload,
    );
  }

  getSpecPackage(
    payload: Omit<KnowledgeGetSpecPackageRequest, 'metaInfo'>,
  ): Promise<KnowledgeResponse> {
    return this.call<KnowledgeGetSpecPackageRequest>(
      PATHS.getSpecPackage,
      'KnowledgeGetSpecPackage',
      'GET_SPEC_PACKAGE',
      payload,
    );
  }

  getSpecPackages(
    payload: Omit<KnowledgeGetSpecPackagesRequest, 'metaInfo'> = {},
  ): Promise<KnowledgeResponse> {
    return this.call<KnowledgeGetSpecPackagesRequest>(
      PATHS.getSpecPackages,
      'KnowledgeGetSpecPackages',
      'GET_SPEC_PACKAGES',
      payload,
    );
  }

  getPackageRevisions(
    payload: Omit<KnowledgeGetPackageRevisionsRequest, 'metaInfo'>,
  ): Promise<KnowledgeResponse> {
    return this.call<KnowledgeGetPackageRevisionsRequest>(
      PATHS.getPackageRevisions,
      'KnowledgeGetPackageRevisions',
      'GET_PACKAGE_REVISIONS',
      payload,
    );
  }

  getLatestRevision(
    payload: Omit<KnowledgeGetLatestRevisionRequest, 'metaInfo'>,
  ): Promise<KnowledgeResponse> {
    return this.call<KnowledgeGetLatestRevisionRequest>(
      PATHS.getLatestRevision,
      'KnowledgeGetLatestRevision',
      'GET_LATEST_REVISION',
      payload,
    );
  }

  getRevisionById(
    payload: Omit<KnowledgeGetRevisionByIdRequest, 'metaInfo'>,
  ): Promise<KnowledgeResponse> {
    return this.call<KnowledgeGetRevisionByIdRequest>(
      PATHS.getRevisionById,
      'KnowledgeGetRevisionById',
      'GET_REVISION_BY_ID',
      payload,
    );
  }

  getSpecPackageDocuments(
    payload: Omit<KnowledgeGetSpecPackageDocumentsRequest, 'metaInfo'>,
  ): Promise<KnowledgeResponse> {
    return this.call<KnowledgeGetSpecPackageDocumentsRequest>(
      PATHS.getSpecPackageDocuments,
      'KnowledgeGetSpecPackageDocuments',
      'GET_SPEC_PACKAGE_DOCUMENTS',
      payload,
    );
  }

  getSpecDoc(
    payload: Omit<KnowledgeGetSpecDocRequest, 'metaInfo'>,
  ): Promise<KnowledgeResponse> {
    return this.call<KnowledgeGetSpecDocRequest>(
      PATHS.getSpecDoc,
      'KnowledgeGetSpecDoc',
      'GET_SPEC_DOC',
      payload,
    );
  }

  listPackages(
    payload: Omit<KnowledgeListPackagesRequest, 'metaInfo'> = {},
  ): Promise<KnowledgeResponse> {
    return this.call<KnowledgeListPackagesRequest>(
      PATHS.listPackages,
      'KnowledgeListPackages',
      'LIST_PACKAGES',
      payload,
    );
  }

  listCollections(
    payload: Omit<KnowledgeListCollectionsRequest, 'metaInfo'> = {},
  ): Promise<KnowledgeResponse> {
    return this.call<KnowledgeListCollectionsRequest>(
      PATHS.listCollections,
      'KnowledgeListCollections',
      'LIST_COLLECTIONS',
      payload,
    );
  }

  getCollectionById(
    payload: Omit<KnowledgeGetCollectionByIdRequest, 'metaInfo'>,
  ): Promise<KnowledgeResponse> {
    return this.call<KnowledgeGetCollectionByIdRequest>(
      PATHS.getCollectionById,
      'KnowledgeGetCollectionById',
      'GET_COLLECTION_BY_ID',
      payload,
    );
  }

  getCollectionLinks(
    payload: Omit<KnowledgeGetCollectionLinksRequest, 'metaInfo'>,
  ): Promise<KnowledgeResponse> {
    return this.call<KnowledgeGetCollectionLinksRequest>(
      PATHS.getCollectionLinks,
      'KnowledgeGetCollectionLinks',
      'GET_COLLECTION_LINKS',
      payload,
    );
  }

  listCollectionRevisions(
    payload: Omit<KnowledgeListCollectionRevisionsRequest, 'metaInfo'>,
  ): Promise<KnowledgeResponse> {
    return this.call<KnowledgeListCollectionRevisionsRequest>(
      PATHS.listCollectionRevisions,
      'KnowledgeListCollectionRevisions',
      'LIST_COLLECTION_REVISIONS',
      payload,
    );
  }

  getCollectionRevisionById(
    payload: Omit<KnowledgeGetCollectionRevisionByIdRequest, 'metaInfo'>,
  ): Promise<KnowledgeResponse> {
    return this.call<KnowledgeGetCollectionRevisionByIdRequest>(
      PATHS.getCollectionRevisionById,
      'KnowledgeGetCollectionRevisionById',
      'GET_COLLECTION_REVISION_BY_ID',
      payload,
    );
  }

  getCollectionRevisionSources(
    payload: Omit<KnowledgeGetCollectionRevisionSourcesRequest, 'metaInfo'>,
  ): Promise<KnowledgeResponse> {
    return this.call<KnowledgeGetCollectionRevisionSourcesRequest>(
      PATHS.getCollectionRevisionSources,
      'KnowledgeGetCollectionRevisionSources',
      'GET_COLLECTION_REVISION_SOURCES',
      payload,
    );
  }

  getCollectionElementRevisions(
    payload: Omit<KnowledgeGetCollectionElementRevisionsRequest, 'metaInfo'>,
  ): Promise<KnowledgeResponse> {
    return this.call<KnowledgeGetCollectionElementRevisionsRequest>(
      PATHS.getCollectionElementRevisions,
      'KnowledgeGetCollectionElementRevisions',
      'GET_COLLECTION_ELEMENT_REVISIONS',
      payload,
    );
  }

  getCollectionRevisionDocuments(
    payload: Omit<KnowledgeGetCollectionRevisionDocumentsRequest, 'metaInfo'>,
  ): Promise<KnowledgeResponse> {
    return this.call<KnowledgeGetCollectionRevisionDocumentsRequest>(
      PATHS.getCollectionRevisionDocuments,
      'KnowledgeGetCollectionRevisionDocuments',
      'GET_COLLECTION_REVISION_DOCUMENTS',
      payload,
    );
  }

  getCollectionRevisionDocumentContent(
    payload: Omit<
      KnowledgeGetCollectionRevisionDocumentContentRequest,
      'metaInfo'
    >,
  ): Promise<KnowledgeResponse> {
    return this.call<KnowledgeGetCollectionRevisionDocumentContentRequest>(
      PATHS.getCollectionRevisionDocumentContent,
      'KnowledgeGetCollectionRevisionDocumentContent',
      'GET_COLLECTION_REVISION_DOCUMENT_CONTENT',
      payload,
    );
  }

  semanticSearch(
    payload: Omit<KnowledgeSemanticSearchRequest, 'metaInfo'>,
  ): Promise<KnowledgeResponse> {
    return this.call<KnowledgeSemanticSearchRequest>(
      PATHS.semanticSearch,
      'KnowledgeSemanticSearch',
      'SEMANTIC_SEARCH',
      payload,
    );
  }
}
